import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AuthTokenService } from './auth-token.service';
import { CurrentUserService } from './current-user.service';
import { ToastService } from './toast.service';
import { isAuthPage } from '../pages/auth/auth-pages';

/**
 * Ends a sign-in when its tokens can no longer be renewed (7 days unused,
 * signed out elsewhere, or a sign-in from before tokens were kept), so the
 * app never shows someone as signed in when the backend can't tell who they
 * are.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private ending = false;

  constructor(
    private tokens: AuthTokenService,
    private currentUser: CurrentUserService,
    private toaster: ToastService,
    private router: Router,
  ) {}

  /** True when the app holds a signed-in user. */
  get signedIn(): boolean {
    try {
      return !!localStorage.getItem('currentUser');
    } catch {
      return false;
    }
  }

  /** On app start: a saved user without a usable session is signed out. */
  checkOnStartup(): void {
    if (this.signedIn && !this.tokens.hasSession) {
      this.end();
    }
  }

  /** Clears the sign-in and asks the user to sign in again (once). */
  end(): void {
    if (this.ending) return;
    this.ending = true;
    const wasSignedIn = this.signedIn;

    this.tokens.clear();
    try {
      localStorage.removeItem('currentUser');
      sessionStorage.removeItem('planUsage');
    } catch {
      /* storage unavailable */
    }
    this.currentUser.setUserData(null);
    this.currentUser.setPlanUsageData(null);
    this.currentUser.setCount(0);

    if (wasSignedIn) {
      // The same once-only "sign in again" message the store uses, which also
      // holds back the generic error of the request that found out.
      this.toaster.showSignInNeeded(true);
    }
    // Don't interrupt a sign-in that's in progress; otherwise sign in again
    // and come back to this page.
    if (!isAuthPage(this.router.url)) {
      this.router.navigate(['/login'], { queryParams: { returnTo: this.router.url } });
    }
    setTimeout(() => (this.ending = false), 1500);
  }
}
